/**
 * AffDice SuperAdmin messages — Overview screen and its popups.
 *
 * Every key starts `overview.` so no other catalogue can collide with it.
 */
import type { Catalogue } from '@affdice/portal-kit';

export const OVERVIEW_MESSAGES = {
  'overview.group.tenantPortfolio': { en: 'Tenant portfolio', es: 'Cartera de clientes', 'pt-BR': 'Carteira de clientes' },
  'overview.group.goLive': {
    en: 'Go-live & compliance',
    es: 'Puesta en marcha y cumplimiento',
    'pt-BR': 'Entrada em operação e compliance',
  },
  'overview.group.health': { en: 'Platform health', es: 'Estado de la plataforma', 'pt-BR': 'Saúde da plataforma' },
  'overview.group.activity': {
    en: 'Activity, platform-wide',
    es: 'Actividad en toda la plataforma',
    'pt-BR': 'Atividade em toda a plataforma',
  },
  'overview.figure.openLabel': {
    en: 'Opens the days behind this figure.',
    es: 'Abre los días que forman esta cifra.',
    'pt-BR': 'Abre os dias que compõem este número.',
  },
  'overview.metric.clicks': { en: 'Clicks', es: 'Clics', 'pt-BR': 'Cliques' },
  'overview.metric.registrations': { en: 'Registrations', es: 'Registros', 'pt-BR': 'Cadastros' },
  'overview.metric.ftds': { en: 'FTDs', es: 'FTDs', 'pt-BR': 'FTDs' },

  'overview.days.title': { en: '{label} — day by day', es: '{label}: día a día', 'pt-BR': '{label}: dia a dia' },
  'overview.days.exportTitle': { en: '{label} day by day', es: '{label} día a día', 'pt-BR': '{label} dia a dia' },
  'overview.days.empty': {
    en: 'Nothing recorded on any day in this range.',
    es: 'No hay nada registrado en ningún día de este rango.',
    'pt-BR': 'Nada registrado em nenhum dia deste intervalo.',
  },
  'overview.days.affiliatesActive': { en: 'Affiliates active', es: 'Afiliados activos', 'pt-BR': 'Afiliados ativos' },
  'overview.days.affiliates': { en: 'Affiliates', es: 'Afiliados', 'pt-BR': 'Afiliados' },
  'overview.days.total': { en: 'Total', es: 'Total', 'pt-BR': 'Total' },
  'overview.days.note': {
    en: 'This card is the sum of these days, from the platform-wide daily rollup. It carries no operator column, so which operator contributed what is not a question this figure can answer.',
    es: 'Esta tarjeta es la suma de estos días, tomada del resumen diario de toda la plataforma. No incluye columna de operador, así que esta cifra no puede decir cuánto aportó cada operador.',
    'pt-BR': 'Este cartão é a soma destes dias, a partir do consolidado diário de toda a plataforma. Ele não tem coluna de operador, então este número não diz quanto cada operador contribuiu.',
  },

  'overview.chart.title': {
    en: 'Clicks and registrations, day by day',
    es: 'Clics y registros, día a día',
    'pt-BR': 'Cliques e cadastros, dia a dia',
  },
  'overview.chart.empty': {
    en: 'No clicks and no registrations recorded yet.',
    es: 'Todavía no hay clics ni registros.',
    'pt-BR': 'Ainda não há cliques nem cadastros.',
  },

  'overview.billing.title': { en: 'Platform billing', es: 'Facturación de la plataforma', 'pt-BR': 'Faturamento da plataforma' },
  'overview.billing.empty': {
    en: 'No platform invoices yet.',
    es: 'Todavía no hay facturas de la plataforma.',
    'pt-BR': 'Ainda não há faturas da plataforma.',
  },
  'overview.billing.issued': { en: 'Issued', es: 'Emitido', 'pt-BR': 'Emitido' },
  'overview.billing.paid': { en: 'Paid', es: 'Pagado', 'pt-BR': 'Pago' },
  'overview.billing.outstanding': { en: 'Outstanding', es: 'Pendiente', 'pt-BR': 'Em aberto' },

  'overview.openOperators': { en: 'Open Operators', es: 'Abrir Operadores', 'pt-BR': 'Abrir Operadores' },
  'overview.openConsumers': { en: 'Open Consumers', es: 'Abrir Consumidores', 'pt-BR': 'Abrir Consumidores' },
  'overview.actionQueue': { en: 'Action queue', es: 'Acciones pendientes', 'pt-BR': 'Ações pendentes' },

  'overview.approvals.title': { en: 'Awaiting approval', es: 'Pendientes de aprobación', 'pt-BR': 'Aguardando aprovação' },
  'overview.approvals.signedUp': {
    en: 'Signed up {date} — {currency}',
    es: 'Registrado el {date} — {currency}',
    'pt-BR': 'Cadastrado em {date} — {currency}',
  },
  'overview.approvals.reasonPlaceholder': {
    en: 'Reason for denying (optional)',
    es: 'Motivo del rechazo (opcional)',
    'pt-BR': 'Motivo da recusa (opcional)',
  },
  'overview.approvals.approve': { en: 'Approve', es: 'Aprobar', 'pt-BR': 'Aprovar' },
  'overview.approvals.deny': { en: 'Deny', es: 'Rechazar', 'pt-BR': 'Recusar' },
} as const satisfies Catalogue;
